import { useEffect, useRef, useState } from 'react'
import type { ConfidenceThresholds } from '../creatives/confidence'
import { createListReads, type ListReads } from '../creatives/reads'
import { type ScopeCounts, scopeCounts } from '../lib/counts'
import type { Period } from '../lib/period'

/**
 * What the nav items render: a number, or no badge at all. `thresholds` are the company's
 * tiers from the same response (GRO-186), for the sidebar legend; `null` until one is held.
 */
export interface ScopeCountsState {
  creatives: number | null
  rollups: number | null
  thresholds: ConfidenceThresholds | null
}

const UNKNOWN: ScopeCountsState = { creatives: null, rollups: null, thresholds: null }
const NOTHING_SELECTED: ScopeCountsState = { creatives: 0, rollups: 0, thresholds: null }

/** A response's counts together with the pair they answer, which is what makes them usable. */
interface HeldCounts extends ScopeCounts {
  scope: string
  reloadKey: number
}

export interface ScopeCountsParams {
  /** The shell's reporting scope key: client, account selection and window. */
  scope: string
  companyId: string
  period: Period
  /** `null` is the company's default scope; `[]` makes no request at all. */
  accountIds: string[] | null
  /**
   * Reporting is visible. False hides the counts and suppresses requests; it does not, by
   * itself, make the held response stale.
   */
  enabled: boolean
  /** The selection on screen still differs from the settled one. Hides, never fetches. */
  settling: boolean
  /** The *settled* selection is empty: nothing behind either nav item, so both are a real `0`. */
  noSelection: boolean
  /** A guest's re-read, or a committed assignment or merge in this company. */
  reloadKey: number
  fetchImpl?: typeof fetch
  /** The shell's shared list reads: the Ideas base request asks the same thing as this badge, and the two share one read in flight. */
  reads?: ListReads
}

/**
 * Both nav badges for the active scope.
 *
 * Two questions are kept apart: whether a count may be shown, and whether a request is
 * needed. Only one thing decides the second - whether a response tagged with the current
 * `scope` and `reloadKey` is held - so the badges stop depending on which screen is
 * mounted and on the filters inside it.
 *
 * A count is a snapshot of the scope, taken when this hook reads it and taken again only
 * by a new `scope` or a new `reloadKey`. A scheduled ingest that commits into the same
 * scope between two reads re-reads the screens when they are next mounted, while the badge
 * keeps its older number until the scope or the `reloadKey` moves. That is the accepted
 * cost of a count the shell owns; the alternative, a bounded invalidation signal between
 * the screens and the shell, is new behaviour rather than a defect fix. So "never a stale
 * number" means never a number from another scope or another `reloadKey`: a number from an
 * earlier read of the same scope is what a badge is.
 */
export function useScopeCounts({
  scope,
  companyId,
  period,
  accountIds,
  enabled,
  settling,
  noSelection,
  reloadKey,
  fetchImpl = fetch,
  reads,
}: ScopeCountsParams): ScopeCountsState {
  // Without the shell's store the hook shares with nobody and keeps its own bodies.
  const own = useRef<ListReads | null>(null)
  const store = reads ?? (own.current ??= createListReads())
  const [held, setHeldState] = useState<HeldCounts | null>(null)
  // The same value for the effect to read without becoming one of its dependencies: the
  // response it holds is not a reason to run the effect, only the scope and the two flags
  // are. A state read there would re-run the effect on every answer, and the cleanup of
  // that run would abort the request the effect had just issued.
  const heldRef = useRef<HeldCounts | null>(null)
  const setHeld = (next: HeldCounts | null) => {
    heldRef.current = next
    setHeldState(next)
  }

  // What the request reads, kept beside the effect so the effect can depend on the scope
  // key alone. `accountIds` is rebuilt by callers that derive it from a checked map, and a
  // fresh array identity would otherwise re-run the effect on every render.
  const request = useRef({ companyId, period, accountIds, fetchImpl })
  request.current = { companyId, period, accountIds, fetchImpl }

  useEffect(() => {
    const current = heldRef.current
    if (noSelection) {
      // Nothing is behind either nav item, so the settled answer is a real zero and no
      // response is needed.
      if (current !== null) setHeld(null)
      return
    }
    // Hidden figures ask for nothing. The held response keeps its own tag, so turning
    // reporting back on fetches only when that tag no longer answers the current scope.
    if (!enabled) return
    // The current scope is already answered, whatever a `settling` flip did in between.
    if (current !== null && current.scope === scope && current.reloadKey === reloadKey) return
    // A scope the reader left loses its response rather than being out-compared: a scope
    // can be returned to, and a count kept for that return would light the old badge over
    // a request that has not landed yet.
    if (current !== null) setHeld(null)

    const read = request.current
    const params = { companyId: read.companyId, period: read.period, accountIds: read.accountIds, audienceId: null, compare: false }
    const tag = { scope, reloadKey }
    // The store shares a read in flight, which is what the Ideas base request is, and
    // nothing retained: a body it kept from an earlier visit to this scope is exactly the
    // count the rule above refuses to light before the new read lands (GRO-177).
    const controller = new AbortController()
    let live = true
    store.read(params, tag, read.fetchImpl, controller.signal)
      .then((response) => {
        // A late answer for a scope or a revision the reader has left is dropped; the tag
        // it is stored under is the pair it was asked for, so this check is the whole rule.
        if (!live) return
        setHeld({ scope, reloadKey, ...scopeCounts(response) })
      })
      .catch((error: unknown) => {
        // An abort is the scope moving or the shell unmounting, not a failed read.
        if (!live || (error instanceof DOMException && error.name === 'AbortError')) return
        // Access problems belong to the mounted screen, which already decides what a 404,
        // a refused scope or a lost session means; a second report from here would race
        // it. A failed count is simply no badge.
        setHeld(null)
      })
    return () => {
      live = false
      controller.abort()
    }
  }, [scope, reloadKey, enabled, noSelection])

  if (!enabled || settling) return UNKNOWN
  if (noSelection) return NOTHING_SELECTED
  // A render that lands between a scope change and the effect that drops the old response
  // reads no badge rather than the previous scope's number.
  if (held === null || held.scope !== scope || held.reloadKey !== reloadKey) return UNKNOWN
  return { creatives: held.creatives, rollups: held.rollups, thresholds: held.thresholds }
}
